/**
 * Organization tab routes and the pure subheader helper.
 *
 * Kept dependency-free so unit tests can import the helper without pulling in
 * the React tab components that constants.ts otherwise loads transitively
 * (which in turn drag in @/lib/api-client and break under ts-jest).
 */

/**
 * ⚠️ KIR-2659 deep-link contract: the `Alerts: 'alerts'` string value
 * is baked into:
 *   - `buildSummaryDeepLinkURL` in extensions/agent/pkg/notifications/coordinator.go
 *   - `buildSummaryDeepLinkURL` in extensions/vscode-core/src/services/notifications/coordinator.ts
 *   - `buildAlertsDeepLinkUrl` in
 *     apps/frontend/src/pages/organization/tabs/DataTableAlertsTab/utils.ts
 * Renaming this literal silently breaks every notification deep-link
 * to the org alerts view AND the in-app callers (LatestAlertsWidget,
 * DataTableUsersTab CellRenderers). Update those four sites in the
 * same PR.
 */
export const OrgTab = {
  Policies: 'policies',
  // KIR-3816: top-level Secure AI Coding Enforcement page. Gated by the
  // ai_instruction_catalog flag. KIR-3925 groups it under the "AI AppSec"
  // sidebar section together with the two tabs below.
  SecureAiCoding: 'secure-ai-coding-enforcement',
  // KIR-3925: AI AppSec pages, gated by the ai_instruction_catalog flag.
  ThreatModeling: 'threat-modeling',
  LlmVulnDiscovery: 'llm-vulnerability-discovery',
  Alerts: 'alerts',
  PostureManagement: 'inventory',
  ShadowAI: 'shadow-ai',
  Users: 'users',
  // KIR-3800: Security Groups list + detail, gated by security_groups_policies.
  SecurityGroups: 'security-groups',
  Settings: 'settings',
  AuditLogs: 'audit-logs',
  Install: 'install',
  AttackDemonstrations: 'attack-demonstrations',
  Dashboard: 'dashboard',
  AgentsActivity: 'agent-activity',
} as const;

export type OrgTabType = (typeof OrgTab)[keyof typeof OrgTab];

/**
 * Get subheader text for an organization tab
 */
export function getSubheaderForTab(
  tab: OrgTabType,
  organizationName: string | undefined
): string {
  const orgName = organizationName || 'your organization';

  switch (tab) {
    case OrgTab.Policies:
      return `Review ${orgName}'s policies`;
    case OrgTab.SecureAiCoding:
      return `Enforce secure coding guidance for AI coding agents across ${orgName}`;
    case OrgTab.ThreatModeling:
      return `Make AI coding agents model threats before writing code across ${orgName}`;
    case OrgTab.LlmVulnDiscovery:
      return 'Find real, exploitable vulnerabilities with OpenAnt';
    case OrgTab.Alerts:
      return `Review security alerts across ${orgName}`;
    case OrgTab.PostureManagement:
      return `Review and manage your inventory with centralized visibility`;
    case OrgTab.ShadowAI:
      return `Monitor AI tools and agents used across ${orgName}`;
    case OrgTab.Users:
      return 'Review and edit users in your organization';
    case OrgTab.SecurityGroups:
      return `Apply different policies to groups of users in ${orgName}`;
    case OrgTab.Settings:
      return 'Manage your organization settings, integrations, notifications, and preferences';
    case OrgTab.AuditLogs:
      return `Review ${orgName}'s audit logs`;
    case OrgTab.Install:
      return 'Review and complete the required steps for setup';
    case OrgTab.AttackDemonstrations:
      return "Review and test Kirin's capabilities by simulation";
    case OrgTab.Dashboard:
      return `Security Overview for ${orgName}`;
    case OrgTab.AgentsActivity:
      return `Packages installed and repositories accessed by coding agents across ${orgName}`;
    default:
      return '';
  }
}
